import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@sakhtar/design-system/react';
import {
  completePasswordRecovery,
  requestPasswordRecoveryCode,
  verifyPasswordRecoveryCode,
} from '../../lib/auth.js';
import { ApiError } from '../../lib/apiClient.js';
import { faDigits } from '../../lib/faDigits.js';
import {
  IRAN_MOBILE_VALIDATION_MESSAGE,
  isValidIranMobileNumber,
  toEnglishDigits,
} from '../../lib/phoneNumber.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useIsDesktopAuth } from './AuthHeroPanel.js';
import { TenantAuthFrame } from './TenantAuthFrame.js';

/**
 * UI_REFERENCE_SOURCE: Sakhtar Login.dc.html tenant credential shell
 *   (`TenantAuthFrame`) + Sakhtar Onboarding Desktop §A4 (6-cell SMS code,
 *   resend countdown) and §A4b (password rules, strength bar, confirmation)
 *   — the recovery screens reuse those exact patterns rather than a new look.
 * status: REAL — backed by `POST /t/:slug/api/auth/session/recovery{,/verify,/complete}`
 *   (apps/api/src/auth/auth-recovery.controller.ts).
 *
 * Flow: mobile → SMS code → new password → done. The request step never says
 * whether the number has an account (ADR-002 AUTH-7): the code screen always
 * follows, and a wrong/expired/unknown code reads the same.
 */

type Step = 'mobile' | 'code' | 'password' | 'done';
type CodeState = 'idle' | 'verifying' | 'incomplete' | 'invalid' | 'rate' | 'net';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;
const MIN_PASSWORD_LENGTH = 8;

const CODE_MESSAGES: Partial<Record<CodeState, string>> = {
  incomplete: 'کد ۶ رقمی را کامل وارد کنید.',
  invalid: 'کد وارد‌شده صحیح نیست یا اعتبار آن به پایان رسیده است.',
  rate: 'تعداد تلاش‌ها زیاد بوده است. چند دقیقه دیگر دوباره امتحان کنید.',
  net: 'بررسی کد انجام نشد. اتصال شما قطع است یا سرویس پاسخ نمی‌دهد.',
};

interface RecoveryLocationState {
  readonly mobileNumber?: string;
}

function maskMobile(mobile: string): string {
  return mobile.length === 11 ? `${mobile.slice(0, 4)} ••• ${mobile.slice(7)}` : mobile;
}

function StepHeader({ title, lead }: { readonly title: string; readonly lead: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--sk-fg)' }}>{title}</h1>
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.9, color: 'var(--sk-fg-muted)' }}>
        {lead}
      </p>
    </div>
  );
}

function ErrorBox({ children }: { readonly children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        gap: 11,
        alignItems: 'flex-start',
        padding: '14px 16px',
        borderRadius: 'var(--sk-r-md)',
        background: 'var(--sk-danger-soft)',
        border: '1px solid color-mix(in oklch, var(--sk-danger), transparent 78%)',
      }}
    >
      <span style={{ color: 'var(--sk-danger)', display: 'flex', paddingTop: 2, flex: '0 0 auto' }}>
        <Icon name="warn" size="sm" />
      </span>
      <span style={{ fontSize: 12, lineHeight: 1.9, color: 'var(--sk-fg)', flex: 1 }}>
        {children}
      </span>
    </div>
  );
}

export function PasswordRecoveryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktopAuth();
  const initialMobile = (location.state as RecoveryLocationState | null)?.mobileNumber ?? '';

  const [step, setStep] = useState<Step>('mobile');
  const [mobile, setMobile] = useState(initialMobile);
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const flowId = useRef<string>(crypto.randomUUID());

  const [otp, setOtp] = useState('');
  const [otpFocused, setOtpFocused] = useState(false);
  const [codeState, setCodeState] = useState<CodeState>('idle');
  const [resendLeft, setResendLeft] = useState(0);
  const [resent, setResent] = useState(false);
  const resetToken = useRef<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errPw, setErrPw] = useState(false);
  const [errPw2, setErrPw2] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const idempotencyKey = useRef(crypto.randomUUID());

  useEffect(() => {
    if (resendLeft <= 0) return;
    const timer = setInterval(() => setResendLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendLeft]);

  if (!slug) return null;
  const tenantSlug = slug;
  const loginPath = `${tenantBasePath(tenantSlug)}/login`;

  async function sendCode(event?: FormEvent) {
    event?.preventDefault();
    setMobileError(null);
    const normalized = toEnglishDigits(mobile.trim());
    if (!isValidIranMobileNumber(normalized)) {
      setMobileError(IRAN_MOBILE_VALIDATION_MESSAGE);
      return;
    }
    setSending(true);
    try {
      // A new number is a new recovery attempt.
      flowId.current = crypto.randomUUID();
      await requestPasswordRecoveryCode(tenantSlug, flowId.current, normalized);
      setMobile(normalized);
      setOtp('');
      setCodeState('idle');
      setResent(false);
      setResendLeft(RESEND_SECONDS);
      setStep('code');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        setMobileError('تعداد درخواست‌ها زیاد بوده است. چند دقیقه دیگر دوباره امتحان کنید.');
      } else if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        setMobileError(IRAN_MOBILE_VALIDATION_MESSAGE);
      } else {
        setMobileError('ارسال کد انجام نشد. اتصال خود را بررسی کنید و دوباره تلاش کنید.');
      }
    } finally {
      setSending(false);
    }
  }

  async function resendCode() {
    setCodeState('idle');
    try {
      await requestPasswordRecoveryCode(tenantSlug, flowId.current, mobile);
      setOtp('');
      setResent(true);
      setResendLeft(RESEND_SECONDS);
    } catch (err) {
      setCodeState(err instanceof ApiError && err.code === 'RATE_LIMITED' ? 'rate' : 'net');
    }
  }

  async function verifyCode(event: FormEvent) {
    event.preventDefault();
    if (otp.length !== OTP_LENGTH) {
      setCodeState('incomplete');
      return;
    }
    setCodeState('verifying');
    try {
      const verified = await verifyPasswordRecoveryCode(tenantSlug, flowId.current, otp);
      resetToken.current = verified.resetToken;
      setCodeState('idle');
      setStep('password');
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.code === 'NOT_FOUND' || err.code === 'VALIDATION_FAILED')
      ) {
        setCodeState('invalid');
      } else if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        setCodeState('rate');
      } else {
        setCodeState('net');
      }
    }
  }

  const passwordRules = [
    {
      label: `حداقل ${faDigits(String(MIN_PASSWORD_LENGTH))} کاراکتر`,
      ok: password.length >= MIN_PASSWORD_LENGTH,
    },
    { label: 'شامل حرف انگلیسی', ok: /[a-zA-Z]/.test(password) },
    { label: 'شامل عدد', ok: /[0-9]/.test(password) },
  ];
  const pwScore = passwordRules.filter((r) => r.ok).length;
  const pwColor =
    pwScore >= 3 ? 'var(--sk-success)' : pwScore === 2 ? 'var(--sk-warn)' : 'var(--sk-danger)';
  const pwLabel = password === '' ? '—' : pwScore >= 3 ? 'قوی' : pwScore === 2 ? 'متوسط' : 'ضعیف';
  const pwMatched = confirmation !== '' && confirmation === password && !errPw2;
  const pwInputType = showPassword ? 'text' : 'password';

  async function savePassword(event: FormEvent) {
    event.preventDefault();
    setSaveError(null);
    if (passwordRules.some((r) => !r.ok)) {
      setErrPw(true);
      return;
    }
    if (confirmation !== password) {
      setErrPw2(true);
      return;
    }
    const token = resetToken.current;
    if (token === null) {
      setStep('mobile');
      return;
    }
    setSaving(true);
    try {
      await completePasswordRecovery(tenantSlug, token, password, idempotencyKey.current);
      resetToken.current = null;
      setStep('done');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'NOT_FOUND') {
        setSaveError(
          'مهلت تعیین رمز جدید به پایان رسیده یا این مرحله قبلاً انجام شده است. لطفاً دوباره کد تأیید بگیرید.',
        );
      } else if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        setErrPw(true);
      } else {
        setSaveError('ذخیرهٔ رمز عبور انجام نشد. اتصال خود را بررسی کنید و دوباره تلاش کنید.');
      }
    } finally {
      setSaving(false);
    }
  }

  const backToLogin = (
    <button
      type="button"
      className="sk-btn sk-btn--ghost sk-btn--sm"
      onClick={() => navigate(loginPath, { state: { mobileNumber: mobile } })}
      style={{ alignSelf: 'center' }}
    >
      بازگشت به صفحهٔ ورود
    </button>
  );

  const resendClock = faDigits(
    `${String(Math.floor(resendLeft / 60)).padStart(2, '0')}:${String(resendLeft % 60).padStart(2, '0')}`,
  );
  const codeBad = codeState === 'invalid' || codeState === 'rate' || codeState === 'net';
  const codeMsg = CODE_MESSAGES[codeState];

  let content: ReactNode;
  if (step === 'mobile') {
    content = (
      <form
        noValidate
        onSubmit={(e) => void sendCode(e)}
        style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
      >
        <StepHeader
          title="بازیابی رمز عبور"
          lead="شماره موبایلی را که با آن وارد فضای کاری می‌شوید وارد کنید تا کد تأیید برایتان پیامک شود."
        />
        {mobileError ? <ErrorBox>{mobileError}</ErrorBox> : null}
        <div className="sk-field">
          <label htmlFor="recovery-mobile">شماره موبایل</label>
          <div className="sk-input-group">
            <span style={{ color: 'var(--sk-fg-subtle)', display: 'flex' }}>
              <Icon name="phone" size="sm" />
            </span>
            <input
              id="recovery-mobile"
              name="username"
              type="tel"
              inputMode="numeric"
              autoComplete="username"
              dir="ltr"
              maxLength={11}
              placeholder="09123456789"
              autoFocus
              value={mobile}
              onChange={(e) => {
                setMobile(e.target.value);
                setMobileError(null);
              }}
              style={{
                direction: 'ltr',
                textAlign: 'left',
                fontWeight: 600,
                letterSpacing: '.03em',
              }}
            />
          </div>
        </div>
        <button
          type="submit"
          className="sk-btn sk-btn--primary sk-btn--lg"
          disabled={sending}
          style={{ width: '100%' }}
        >
          {sending ? (
            <>
              <span className="sk-spin" style={{ width: 16, height: 16 }} />
              در حال ارسال…
            </>
          ) : (
            'ارسال کد تأیید'
          )}
        </button>
        {backToLogin}
      </form>
    );
  } else if (step === 'code') {
    content = (
      <form
        noValidate
        onSubmit={(e) => void verifyCode(e)}
        style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
      >
        <StepHeader
          title="کد تأیید را وارد کنید"
          lead={
            <>
              اگر این شماره در این فضای کاری حساب داشته باشد، کد ۶ رقمی به{' '}
              <span
                style={{
                  fontWeight: 600,
                  color: 'var(--sk-fg)',
                  direction: 'ltr',
                  display: 'inline-block',
                }}
              >
                {faDigits(maskMobile(mobile))}
              </span>{' '}
              پیامک می‌شود.
            </>
          }
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ position: 'relative', width: '100%' }}>
            <div
              style={{ display: 'flex', gap: isDesktop ? 10 : 8, direction: 'ltr' }}
              aria-hidden="true"
            >
              {Array.from({ length: OTP_LENGTH }, (_, i) => {
                const active = otpFocused && !codeBad && i === Math.min(otp.length, OTP_LENGTH - 1);
                return (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      height: isDesktop ? 60 : 54,
                      borderRadius: 'var(--sk-r-md)',
                      border: `1.5px solid ${
                        codeBad
                          ? 'var(--sk-danger)'
                          : active
                            ? 'var(--sk-brand-500)'
                            : 'var(--sk-border)'
                      }`,
                      background: codeBad ? 'var(--sk-danger-soft)' : 'var(--sk-bg-canvas)',
                      boxShadow: 'var(--sk-inset-2)',
                      display: 'grid',
                      placeContent: 'center',
                      fontSize: 24,
                      fontWeight: 600,
                      color: 'var(--sk-fg)',
                    }}
                  >
                    {otp[i] ? faDigits(otp[i]) : ''}
                  </div>
                );
              })}
            </div>
            <input
              aria-label="کد تأیید پیامکی"
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
              maxLength={OTP_LENGTH}
              value={otp}
              onFocus={() => setOtpFocused(true)}
              onBlur={() => setOtpFocused(false)}
              onChange={(e) => {
                setOtp(toEnglishDigits(e.target.value).replace(/\D/g, '').slice(0, OTP_LENGTH));
                setCodeState('idle');
              }}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                border: 0,
                cursor: 'text',
              }}
            />
          </div>
          {codeMsg ? (
            <span
              role="alert"
              style={{
                fontSize: 13,
                lineHeight: 1.8,
                color: codeState === 'rate' ? 'var(--sk-warn)' : 'var(--sk-danger)',
              }}
            >
              {codeMsg}
            </span>
          ) : null}
          {resent && !codeMsg ? (
            <span style={{ fontSize: 12, lineHeight: 1.8, color: 'var(--sk-fg-muted)' }}>
              کد جدید درخواست شد. اگر پیامکی نرسید، چند دقیقه بعد دوباره تلاش کنید.
            </span>
          ) : null}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              borderTop: '1px solid var(--sk-border)',
              paddingTop: 14,
            }}
          >
            {resendLeft <= 0 ? (
              <button
                type="button"
                className="sk-btn sk-btn--secondary sk-btn--sm"
                onClick={() => void resendCode()}
              >
                ارسال مجدد کد
              </button>
            ) : (
              <>
                <span style={{ fontSize: 13, color: 'var(--sk-fg-subtle)' }}>ارسال مجدد کد</span>
                <span
                  className="sk-num"
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--sk-fg-muted)',
                    direction: 'ltr',
                  }}
                >
                  {resendClock}
                </span>
              </>
            )}
            <button
              type="button"
              className="sk-btn sk-btn--ghost sk-btn--sm"
              onClick={() => {
                setStep('mobile');
                setCodeState('idle');
              }}
              style={{ marginInlineStart: 'auto' }}
            >
              تغییر شماره
            </button>
          </div>
        </div>
        <button
          type="submit"
          className="sk-btn sk-btn--primary sk-btn--lg"
          disabled={codeState === 'verifying'}
          style={{ width: '100%' }}
        >
          {codeState === 'verifying' ? (
            <>
              <span className="sk-spin" style={{ width: 16, height: 16 }} />
              در حال بررسی…
            </>
          ) : (
            'تأیید کد'
          )}
        </button>
        {backToLogin}
      </form>
    );
  } else if (step === 'password') {
    content = (
      <form
        noValidate
        onSubmit={(e) => void savePassword(e)}
        style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
      >
        <StepHeader
          title="رمز عبور جدید"
          lead="شماره موبایل شما تأیید شد. یک رمز عبور تازه برای ورود به فضای کاری انتخاب کنید."
        />
        <input
          type="text"
          name="username"
          autoComplete="username"
          value={mobile}
          readOnly
          hidden
          tabIndex={-1}
        />
        {saveError ? <ErrorBox>{saveError}</ErrorBox> : null}
        <div className="sk-field">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label htmlFor="recovery-pw" style={{ margin: 0 }}>
              رمز عبور جدید
            </label>
            <button
              type="button"
              className="sk-btn sk-btn--ghost sk-btn--sm"
              onClick={() => setShowPassword((v) => !v)}
              style={{ marginInlineStart: 'auto', height: 26 }}
            >
              {showPassword ? 'پنهان کردن' : 'نمایش رمز'}
            </button>
          </div>
          <input
            id="recovery-pw"
            className="sk-input"
            type={pwInputType}
            required
            autoFocus
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrPw(false);
              setErrPw2(false);
            }}
            placeholder={`حداقل ${faDigits(String(MIN_PASSWORD_LENGTH))} کاراکتر`}
            style={{ direction: 'ltr', textAlign: 'left', letterSpacing: '.04em' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 4 }}>
            <div className="sk-prog" style={{ flex: 1 }}>
              <div
                className="sk-prog__fill"
                style={{ width: `${pwScore * 33.4}%`, background: pwColor }}
              />
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: pwColor, whiteSpace: 'nowrap' }}>
              {pwLabel}
            </span>
          </div>
          {errPw ? (
            <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>
              رمز عبور شرایط لازم را ندارد.
            </span>
          ) : null}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {passwordRules.map((rule) => (
            <div key={rule.label} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              {rule.ok ? (
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: 'var(--sk-success)',
                    color: '#fff',
                    display: 'grid',
                    placeContent: 'center',
                    flex: '0 0 auto',
                    fontSize: 11,
                  }}
                >
                  <Icon name="check" size="sm" />
                </span>
              ) : (
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '1.5px solid var(--sk-border-strong)',
                    flex: '0 0 auto',
                  }}
                />
              )}
              <span
                style={{ fontSize: 13, color: rule.ok ? 'var(--sk-fg)' : 'var(--sk-fg-subtle)' }}
              >
                {rule.label}
              </span>
            </div>
          ))}
        </div>
        <div
          className="sk-field"
          style={{ borderTop: '1px solid var(--sk-border)', paddingTop: 16 }}
        >
          <label htmlFor="recovery-pw2">تکرار رمز عبور</label>
          <input
            id="recovery-pw2"
            className="sk-input"
            type={pwInputType}
            required
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => {
              setConfirmation(e.target.value);
              setErrPw2(false);
            }}
            placeholder="رمز عبور را دوباره وارد کنید"
            style={{ direction: 'ltr', textAlign: 'left', letterSpacing: '.04em' }}
          />
          {errPw2 ? (
            <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>
              دو رمز وارد‌شده یکسان نیستند.
            </span>
          ) : null}
          {pwMatched ? (
            <span style={{ fontSize: 12, color: 'var(--sk-success)' }}>رمز عبور تکرار شد.</span>
          ) : null}
        </div>
        <button
          type="submit"
          className="sk-btn sk-btn--primary sk-btn--lg"
          disabled={saving}
          style={{ width: '100%' }}
        >
          {saving ? (
            <>
              <span className="sk-spin" style={{ width: 16, height: 16 }} />
              در حال ذخیره…
            </>
          ) : (
            'ذخیرهٔ رمز عبور'
          )}
        </button>
        {saveError ? (
          <button
            type="button"
            className="sk-btn sk-btn--ghost sk-btn--sm"
            onClick={() => {
              setSaveError(null);
              setStep('mobile');
            }}
            style={{ alignSelf: 'center' }}
          >
            دریافت کد تأیید جدید
          </button>
        ) : null}
      </form>
    );
  } else {
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
        <span
          style={{
            width: 64,
            height: 64,
            borderRadius: 20,
            background: '#d6f0e2',
            color: '#2e7d5b',
            display: 'grid',
            placeContent: 'center',
            boxShadow: '0 12px 24px -10px rgba(46,125,91,.28)',
          }}
        >
          <Icon name="check" size="lg" />
        </span>
        <StepHeader
          title="رمز عبور تغییر کرد"
          lead="رمز عبور جدید ثبت شد و همهٔ نشست‌های قبلی این حساب بسته شدند. اکنون با رمز جدید وارد شوید."
        />
        <button
          type="button"
          className="sk-btn sk-btn--primary sk-btn--lg"
          onClick={() => navigate(loginPath, { replace: true, state: { mobileNumber: mobile } })}
          style={{ width: '100%' }}
        >
          ورود به فضای کاری
        </button>
      </div>
    );
  }

  return <TenantAuthFrame isDesktop={isDesktop}>{content}</TenantAuthFrame>;
}
